<script setup>
import { computed, useSlots } from 'vue';
import { Loader2 } from 'lucide-vue-next';

const props = defineProps({
  variant: {
    type: String,
    default: 'empty', // 'loading' | 'empty'
  },
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  fullHeight: {
    type: Boolean,
    default: false,
  },
});

const slots = useSlots();

const containerClass = computed(() => {
  const classes = [
    'w-full',
    'h-full',
    'flex',
    'flex-col',
    'items-center',
    'justify-center',
    'text-center',
    'gap-4',
    'px-6',
  ];

  if (props.fullHeight) {
    classes.push('min-h-[calc(100vh-240px)]');
  }

  return classes;
});

const hasActions = computed(() => Boolean(slots.actions));
const hasIconSlot = computed(() => Boolean(slots.icon));
</script>

<template>
  <div :class="containerClass">
    <template v-if="variant === 'loading'">
      <Loader2 class="h-8 w-8 text-black/50 animate-spin" />
      <p class="text-sm font-light text-black/50 tracking-tight">loading</p>
    </template>
    <template v-else>
      <div class="flex flex-col items-center gap-4">
        <div
          class="w-16 h-16 rounded-2xl flex items-center justify-center bg-transparent text-black/30"
        >
          <slot v-if="hasIconSlot" name="icon" />
          <svg
            v-else
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="text-black/20"
          >
            <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.5" />
            <path
              d="M13.5 8.5L10 12L13 15.5"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <div class="space-y-2 max-w-md">
          <p v-if="title" class="text-base font-light text-black/40 tracking-tight">
            {{ title }}
          </p>
          <p v-if="description" class="text-sm text-black/40">
            {{ description }}
          </p>
        </div>
        <div v-if="hasActions" class="flex items-center justify-center gap-3 mt-2">
          <slot name="actions" />
        </div>
      </div>
    </template>
  </div>
</template>
